﻿import React from 'react'
import { Button, Text, Input, LoadingSpinner, Heading, Select } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { useAuthContext } from '../../contexts/AuthContext'
import { usePermission } from '../../hooks/usePermissions'
import { modulesService } from '../../services/modules'
import {
  brokerCadastroChangeRequestsService,
  type ApplyChangeRequestResult,
  type BrokerChangeRequestPayload,
  type BrokerChangeRequestStatus,
  type BrokerChangeRequestType,
  type ChangeRequestBaseline,
  type HelpdeskBrokerChangeRequest,
} from '../../services/brokerCadastroChangeRequests'
import { comercialGestaoService } from '../../services/comercialGestao'
import { userLogsService } from '../../services/userLogs'
import type { ComercialUser } from '../../types/comercial'

const MODULE_NAME = 'comercial_atualizacao-cadastral'

type StatusFilter = BrokerChangeRequestStatus | 'all'
type RequestTypeFilter = BrokerChangeRequestType | 'all'

const requestTypeLabels: Record<BrokerChangeRequestType, string> = {
  broker_profile: 'Cadastro do corretor',
  contact_create: 'Criar contato',
  contact_update: 'Atualizar contato',
  contact_set_active: 'Ativar/desativar contato',
  contact_delete: 'Excluir contato',
}

const statusLabels: Record<BrokerChangeRequestStatus, string> = {
  pending: 'Pendente',
  applied: 'Aplicada',
  rejected: 'Rejeitada',
  cancelled: 'Cancelada',
}

const payloadLabels: Record<string, string> = {
  broker_id: 'Corretor',
  holder_name: 'Nome do titular',
  phone: 'Telefone',
  email: 'E-mail',
  notes: 'Observações',
  contact_id: 'Contato',
  contact_code: 'Código SCGP',
  name: 'Nome',
  whatsapp: 'WhatsApp',
  job_title: 'Cargo',
  department: 'Departamento',
  is_active: 'Ativo',
  is_partner: 'Sócio',
}

const HIDDEN_DIFF_KEYS = new Set(['broker_id', 'contact_id'])

const BROKER_PROFILE_FIELD_KEYS = ['holder_name', 'phone', 'email', 'notes'] as const

const CONTACT_FIELD_KEYS = [
  'name',
  'phone',
  'whatsapp',
  'email',
  'job_title',
  'is_partner',
  'is_active',
  'notes',
] as const

type ChangeDiffMode = 'change' | 'add' | 'remove'

type ChangeDiffRow = {
  key: string
  label: string
  beforeText: string | null
  afterText: string | null
  mode: ChangeDiffMode
}

const diffBeforeClass =
  'rounded-md px-2 py-1.5 text-sm bg-red-50 text-red-800 line-through decoration-red-400/80 dark:bg-red-950/45 dark:text-red-200 dark:decoration-red-400/60'

const diffAfterClass =
  'rounded-md px-2 py-1.5 text-sm bg-emerald-50 text-emerald-900 font-medium dark:bg-emerald-950/45 dark:text-emerald-100'

const diffAddOnlyClass =
  'rounded-md px-2 py-1.5 text-sm bg-emerald-50 text-emerald-900 font-medium dark:bg-emerald-950/45 dark:text-emerald-100'

const diffRemoveOnlyClass =
  'rounded-md px-2 py-1.5 text-sm bg-red-50 text-red-800 line-through decoration-red-400/80 dark:bg-red-950/45 dark:text-red-200'

const diffEmptyClass = 'text-sm text-gray-400 dark:text-gray-500'

const btnApplyOutlineClass =
  '!text-emerald-700 hover:!bg-emerald-50 dark:!text-emerald-400 dark:hover:!bg-emerald-950/40 !ring-emerald-300 hover:!ring-emerald-400 dark:!ring-emerald-800/60 dark:hover:!ring-emerald-700/80'

const btnRejectOutlineClass =
  '!text-red-600 hover:!bg-red-50 dark:!text-red-400 dark:hover:!bg-red-950/40 !ring-red-300 hover:!ring-red-400 dark:!ring-red-900/60 dark:hover:!ring-red-700/80'

const btnCancelOutlineClass =
  '!text-pink-700 hover:!bg-pink-50 dark:!text-pink-400 dark:hover:!bg-pink-950/40 !ring-pink-300 hover:!ring-pink-400 dark:!ring-pink-900/60 dark:hover:!ring-pink-700/80'

const btnRefreshOutlineClass =
  '!text-primary-600 hover:!bg-primary-50 dark:!text-primary-400 dark:hover:!bg-primary-950/40 !ring-primary-300 hover:!ring-primary-400 dark:!ring-primary-900/60 dark:hover:!ring-primary-700/80'

const btnFilterOutlineClass =
  '!text-indigo-700 hover:!bg-indigo-50 dark:!text-indigo-400 dark:hover:!bg-indigo-950/40 !ring-indigo-300 hover:!ring-indigo-400 dark:!ring-indigo-900/60 dark:hover:!ring-indigo-700/80'

const btnDetailsOutlineClass =
  '!text-slate-700 hover:!bg-slate-50 dark:!text-slate-300 dark:hover:!bg-slate-800/50 !ring-slate-300 hover:!ring-slate-400 dark:!ring-slate-600 dark:hover:!ring-slate-500'

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string' && message.trim()) return message
  }
  return 'Erro inesperado ao carregar solicitações.'
}

function formatDateTime(iso?: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function statusBadgeClass(status: BrokerChangeRequestStatus): string {
  switch (status) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200'
    case 'applied':
      return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
    case 'rejected':
      return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
    case 'cancelled':
      return 'bg-pink-100 text-pink-900 dark:bg-pink-950/55 dark:text-pink-100'
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
  }
}

function valueToText(value: unknown): string {
  if (value == null) return '—'
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não'
  if (typeof value === 'object') return JSON.stringify(value, null, 2)
  return String(value)
}

function payloadRecord(payload: BrokerChangeRequestPayload): Record<string, unknown> {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return {}
  return payload as Record<string, unknown>
}

function fieldLabel(key: string): string {
  return payloadLabels[key] ?? key
}

function resolveAfterValue(
  key: string,
  payload: Record<string, unknown>,
  baseline: ChangeRequestBaseline | null,
  mode: ChangeDiffMode
): string | null {
  if (mode === 'remove') return null
  if (mode === 'add') return valueToText(payload[key])
  if (Object.prototype.hasOwnProperty.call(payload, key)) {
    return valueToText(payload[key])
  }
  return valueToText(baseline?.[key])
}

function buildFieldDiffRows(
  fieldKeys: readonly string[],
  payload: Record<string, unknown>,
  baseline: ChangeRequestBaseline | null,
  mode: ChangeDiffMode
): ChangeDiffRow[] {
  return fieldKeys.map((key) => ({
    key,
    label: fieldLabel(key),
    beforeText: mode === 'add' ? null : valueToText(baseline?.[key]),
    afterText: resolveAfterValue(key, payload, baseline, mode),
    mode,
  }))
}

function buildChangeDiffRows(
  request: HelpdeskBrokerChangeRequest,
  baseline: ChangeRequestBaseline | null
): ChangeDiffRow[] {
  const type = request.request_type
  const payload = payloadRecord(request.payload)

  switch (type) {
    case 'broker_profile':
      return buildFieldDiffRows(BROKER_PROFILE_FIELD_KEYS, payload, baseline, 'change')
    case 'contact_create':
      return buildFieldDiffRows(CONTACT_FIELD_KEYS, payload, baseline, 'add')
    case 'contact_delete':
      return buildFieldDiffRows(CONTACT_FIELD_KEYS, payload, baseline, 'remove')
    case 'contact_set_active':
    case 'contact_update':
      return buildFieldDiffRows(CONTACT_FIELD_KEYS, payload, baseline, 'change')
    default:
      return Object.entries(payload)
        .filter(([key]) => !HIDDEN_DIFF_KEYS.has(key))
        .map(([key, value]) => ({
          key,
          label: fieldLabel(key),
          beforeText: valueToText(baseline?.[key]),
          afterText: valueToText(value),
          mode: 'change' as const,
        }))
  }
}

function RequestChangeDiffTable({ rows }: { rows: ChangeDiffRow[] }) {
  if (rows.length === 0) {
    return (
      <div className="p-4">
        <Text color="muted">Nenhum campo identificado nesta solicitação.</Text>
      </div>
    )
  }

  const showBeforeColumn = rows.some((row) => row.mode !== 'add')
  const showAfterColumn = rows.some((row) => row.mode !== 'remove')

  return (
    <div className="overflow-x-auto">
      <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 bg-gray-50 px-4 py-2 dark:border-gray-700 dark:bg-gray-900/40">
        {showBeforeColumn && (
          <span className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
            <span className="h-2.5 w-2.5 rounded-sm bg-red-200 dark:bg-red-800" />
            Valor anterior
          </span>
        )}
        {showAfterColumn && (
          <span className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
            <span className="h-2.5 w-2.5 rounded-sm bg-emerald-200 dark:bg-emerald-800" />
            Novo valor
          </span>
        )}
      </div>
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-white dark:bg-gray-800">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Campo
            </th>
            {showBeforeColumn && (
              <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-red-700/90 dark:text-red-300/90">
                Antes
              </th>
            )}
            {showAfterColumn && (
              <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-emerald-700/90 dark:text-emerald-300/90">
                Depois
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
          {rows.map((row) => (
            <tr key={row.key}>
              <td className="px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200">{row.label}</td>
              {showBeforeColumn && (
                <td className="px-4 py-3 align-top">
                  {row.mode === 'add' ? (
                    <span className={diffEmptyClass}>—</span>
                  ) : row.beforeText != null && row.beforeText !== '—' ? (
                    <div className={row.mode === 'remove' ? diffRemoveOnlyClass : diffBeforeClass}>
                      {row.beforeText}
                    </div>
                  ) : (
                    <span className={diffEmptyClass}>—</span>
                  )}
                </td>
              )}
              {showAfterColumn && (
                <td className="px-4 py-3 align-top">
                  {row.mode === 'remove' ? (
                    <span className={`${diffEmptyClass} italic`}>Será removido</span>
                  ) : row.afterText != null && row.afterText !== '—' ? (
                    <div className={row.mode === 'add' ? diffAddOnlyClass : diffAfterClass}>{row.afterText}</div>
                  ) : (
                    <span className={diffEmptyClass}>—</span>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function brokerLabel(request: HelpdeskBrokerChangeRequest): string {
  const name = request.broker?.name?.trim() || 'Corretor sem nome'
  const scgp = request.broker?.id_scgp != null ? `SCGP ${request.broker.id_scgp}` : 'sem SCGP'
  return `${name} (${scgp})`
}

const COMERCIAL_ATUALIZACAO_CADASTRAL_LOG_ORIGEM = 'comercial/atualizacao-cadastral'

const SCGP_APPLY_CONFIRM_MESSAGE =
  'Tem certeza que deseja atualizar estes dados cadastrais do corretor no SCGP?'

/** Registra em `user_activity_logs` após aplicar solicitação (SCGP). */
async function logBrokerChangeRequestApply(
  previous: HelpdeskBrokerChangeRequest,
  result: ApplyChangeRequestResult
): Promise<void> {
  try {
    const tipoLabel = requestTypeLabels[previous.request_type]
    await userLogsService.logUpdate(
      'other',
      result.id,
      `Aplicou solicitação de atualização cadastral no SCGP (${tipoLabel}) — ${brokerLabel(previous)}.`,
      {
        status: 'pending',
        request_type: previous.request_type,
        broker_id: previous.broker_id,
      },
      {
        status: result.status,
        processed_at: result.processed_at,
        processed_by: result.processed_by,
        rejection_reason: result.rejection_reason,
      },
      {
        origem: COMERCIAL_ATUALIZACAO_CADASTRAL_LOG_ORIGEM,
        change_request_id: result.id,
        aplicacao_scgp_confirmada: true,
        scgp_gerenciar_portal: result.scgpGerenciarPortal != null,
        tipoOperacao: result.scgpGerenciarPortal?.tipoOperacao ?? null,
      }
    )
  } catch (logErr) {
    console.warn('Erro ao registrar log (atualização cadastral — aplicar):', logErr)
  }
}

/** Registra em `user_activity_logs` após cancelar ou rejeitar (tela Configurações > Logs). */
async function logBrokerChangeRequestCancelOrReject(
  kind: 'cancel' | 'reject',
  previous: HelpdeskBrokerChangeRequest,
  result: HelpdeskBrokerChangeRequest,
  reasonTrimmed: string
): Promise<void> {
  try {
    const tipoLabel = requestTypeLabels[previous.request_type]
    const verb = kind === 'cancel' ? 'Cancelou' : 'Rejeitou'
    await userLogsService.logUpdate(
      'other',
      result.id,
      `${verb} solicitação de atualização cadastral (${tipoLabel}) — ${brokerLabel(previous)}.`,
      {
        status: 'pending',
        request_type: previous.request_type,
        broker_id: previous.broker_id,
      },
      {
        status: result.status,
        processed_at: result.processed_at,
        processed_by: result.processed_by,
        rejection_reason: result.rejection_reason,
      },
      {
        origem: COMERCIAL_ATUALIZACAO_CADASTRAL_LOG_ORIGEM,
        change_request_id: result.id,
        motivo_confirmacao: reasonTrimmed,
      }
    )
  } catch (logErr) {
    console.warn('Erro ao registrar log (atualização cadastral):', logErr)
  }
}

const cancelReasonTextareaClass =
  'w-full min-h-[100px] rounded-lg px-3 py-2 text-sm text-gray-900 placeholder:text-gray-500 shadow-sm ring-1 ring-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500/20 disabled:bg-gray-50 dark:bg-white/5 dark:text-white dark:placeholder:text-gray-400 dark:ring-white/10 dark:focus:ring-gray-400/20 dark:disabled:bg-white/5'

type PendingActionModalState =
  | { kind: 'cancel'; request: HelpdeskBrokerChangeRequest }
  | { kind: 'reject'; request: HelpdeskBrokerChangeRequest }

const AtualizacaoCadastral: React.FC = () => {
  const { profile } = useAuthContext()
  const [loading, setLoading] = React.useState(true)
  const [requests, setRequests] = React.useState<HelpdeskBrokerChangeRequest[]>([])
  const [comercialUsers, setComercialUsers] = React.useState<ComercialUser[]>([])
  const [assignmentByBrokerId, setAssignmentByBrokerId] = React.useState<
    Map<string, { comercialUserId: string; name: string }>
  >(() => new Map())
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('all')
  const [typeFilter, setTypeFilter] = React.useState<RequestTypeFilter>('all')
  const [filtroIdScgp, setFiltroIdScgp] = React.useState('')
  const [filtroNomeCorretor, setFiltroNomeCorretor] = React.useState('')
  const [filtroComercialUserId, setFiltroComercialUserId] = React.useState('')
  const [currentPage, setCurrentPage] = React.useState(1)
  const [itemsPerPage] = React.useState(20)
  const [selectedRequest, setSelectedRequest] = React.useState<HelpdeskBrokerChangeRequest | null>(null)
  const [pageError, setPageError] = React.useState<string | null>(null)
  const [actionError, setActionError] = React.useState<string | null>(null)
  const [submittingPendingAction, setSubmittingPendingAction] = React.useState(false)
  const [submittingApply, setSubmittingApply] = React.useState(false)
  const [applyingRequestId, setApplyingRequestId] = React.useState<string | null>(null)
  const [moduleInfo, setModuleInfo] = React.useState<{ display_name: string; description?: string | null } | null>(null)
  const [pendingActionModal, setPendingActionModal] = React.useState<PendingActionModalState | null>(null)
  const [pendingActionReason, setPendingActionReason] = React.useState('')
  const [pendingActionReasonError, setPendingActionReasonError] = React.useState<string | null>(null)
  const [applyConfirmTarget, setApplyConfirmTarget] = React.useState<HelpdeskBrokerChangeRequest | null>(null)
  const [applyModalError, setApplyModalError] = React.useState<string | null>(null)
  const [detailBaseline, setDetailBaseline] = React.useState<ChangeRequestBaseline | null>(null)
  const [detailBaselineLoading, setDetailBaselineLoading] = React.useState(false)
  const [detailBaselineError, setDetailBaselineError] = React.useState<string | null>(null)
  const { hasPermission: canCancel, loading: permissionsLoading } = usePermission(MODULE_NAME, 'update')
  const { hasPermission: canViewValues, loading: loadingViewValues } = usePermission(MODULE_NAME, 'view_values')

  const loggedUserId = profile?.id?.trim() ?? ''
  const loggedUserName = profile?.name?.trim() ?? ''
  const isComercialFilterRestricted = !loadingViewValues && !canViewValues
  const displayedComercialUserId = isComercialFilterRestricted ? loggedUserId : filtroComercialUserId
  const effectiveComercialUserId = isComercialFilterRestricted
    ? loggedUserId || '\0'
    : filtroComercialUserId

  const comercialUsersOptions = React.useMemo(() => {
    if (
      isComercialFilterRestricted &&
      loggedUserId &&
      !comercialUsers.some((u) => u.id === loggedUserId)
    ) {
      return [{ id: loggedUserId, name: loggedUserName || 'Usuário logado', is_active: true }, ...comercialUsers]
    }
    return comercialUsers
  }, [comercialUsers, isComercialFilterRestricted, loggedUserId, loggedUserName])

  const load = React.useCallback(async () => {
    setLoading(true)
    setPageError(null)
    try {
      const [rows, users] = await Promise.all([
        brokerCadastroChangeRequestsService.listChangeRequests(),
        comercialGestaoService.listComercialUsers(),
      ])
      const assignmentMap = await comercialGestaoService.getBrokerComercialAssignmentMapForBrokerIds(
        rows.map((row) => row.broker_id)
      )
      setRequests(rows)
      setComercialUsers(users)
      setAssignmentByBrokerId(assignmentMap)
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void load()
  }, [load])

  React.useEffect(() => {
    if (loadingViewValues) return
    if (!canViewValues && loggedUserId) {
      setFiltroComercialUserId(loggedUserId)
    }
  }, [loadingViewValues, canViewValues, loggedUserId])

  React.useEffect(() => {
    const loadModule = async () => {
      const result = await modulesService.getByName(MODULE_NAME)
      if (result.success && result.data) {
        setModuleInfo({
          display_name: result.data.display_name ?? 'Atualização cadastral',
          description: result.data.description ?? null,
        })
      }
    }
    void loadModule()
  }, [])

  React.useEffect(() => {
    if (!selectedRequest) {
      setDetailBaseline(null)
      setDetailBaselineError(null)
      setDetailBaselineLoading(false)
      return
    }

    if (selectedRequest.request_type === 'contact_create') {
      setDetailBaseline(null)
      setDetailBaselineError(null)
      setDetailBaselineLoading(false)
      return
    }

    let cancelled = false
    setDetailBaselineLoading(true)
    setDetailBaselineError(null)

    void brokerCadastroChangeRequestsService
      .getChangeRequestBaseline(selectedRequest)
      .then((data) => {
        if (!cancelled) setDetailBaseline(data)
      })
      .catch((error) => {
        if (!cancelled) {
          setDetailBaseline(null)
          setDetailBaselineError(getErrorMessage(error))
        }
      })
      .finally(() => {
        if (!cancelled) setDetailBaselineLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [selectedRequest?.id, selectedRequest?.status, selectedRequest?.request_type])

  const detailDiffRows = React.useMemo(() => {
    if (!selectedRequest) return []
    return buildChangeDiffRows(selectedRequest, detailBaseline)
  }, [selectedRequest, detailBaseline])

  const requestsByResponsavel = React.useMemo(() => {
    if (!effectiveComercialUserId) return requests
    return requests.filter((request) => {
      const assignment = assignmentByBrokerId.get(request.broker_id)
      return assignment?.comercialUserId === effectiveComercialUserId
    })
  }, [requests, assignmentByBrokerId, effectiveComercialUserId])

  const filtered = React.useMemo(() => {
    const idScgpStr = filtroIdScgp.trim()
    const nomeCorretor = filtroNomeCorretor.toLowerCase().trim()
    return requestsByResponsavel.filter((request) => {
      if (statusFilter !== 'all' && request.status !== statusFilter) return false
      if (typeFilter !== 'all' && request.request_type !== typeFilter) return false

      if (idScgpStr) {
        if (request.broker?.id_scgp == null || !String(request.broker.id_scgp).includes(idScgpStr)) {
          return false
        }
      }

      if (nomeCorretor) {
        if (!(request.broker?.name ?? '').toLowerCase().includes(nomeCorretor)) return false
      }

      return true
    })
  }, [requestsByResponsavel, statusFilter, typeFilter, filtroIdScgp, filtroNomeCorretor])

  React.useEffect(() => {
    setCurrentPage(1)
  }, [statusFilter, typeFilter, filtroIdScgp, filtroNomeCorretor, displayedComercialUserId])

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage))
  const pageRows = React.useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage
    return filtered.slice(start, start + itemsPerPage)
  }, [filtered, currentPage, itemsPerPage])

  React.useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages)
  }, [currentPage, totalPages])

  const counts = React.useMemo(() => ({
    pending: requestsByResponsavel.filter((request) => request.status === 'pending').length,
    applied: requestsByResponsavel.filter((request) => request.status === 'applied').length,
    rejected: requestsByResponsavel.filter((request) => request.status === 'rejected').length,
    cancelled: requestsByResponsavel.filter((request) => request.status === 'cancelled').length,
  }), [requestsByResponsavel])

  const clearFilters = () => {
    setStatusFilter('all')
    setTypeFilter('all')
    setFiltroIdScgp('')
    setFiltroNomeCorretor('')
    if (!isComercialFilterRestricted) {
      setFiltroComercialUserId('')
    }
    setCurrentPage(1)
  }

  const closePendingActionModal = React.useCallback(() => {
    setPendingActionModal(null)
    setPendingActionReason('')
    setPendingActionReasonError(null)
  }, [])

  const closeDetail = () => {
    closePendingActionModal()
    setApplyConfirmTarget(null)
    setApplyModalError(null)
    setSelectedRequest(null)
    setActionError(null)
  }

  const canActOnPendingRequest = React.useCallback(
    (request: HelpdeskBrokerChangeRequest) => canCancel && request.status === 'pending',
    [canCancel]
  )

  const openCancelFlow = (request: HelpdeskBrokerChangeRequest) => {
    if (!canActOnPendingRequest(request)) return
    setApplyConfirmTarget(null)
    setApplyModalError(null)
    setPendingActionModal({ kind: 'cancel', request })
    setPendingActionReason('')
    setPendingActionReasonError(null)
    setActionError(null)
  }

  const openRejectFlow = (request: HelpdeskBrokerChangeRequest) => {
    if (!canActOnPendingRequest(request)) return
    setApplyConfirmTarget(null)
    setApplyModalError(null)
    setPendingActionModal({ kind: 'reject', request })
    setPendingActionReason('')
    setPendingActionReasonError(null)
    setActionError(null)
  }

  const submitPendingActionReason = async () => {
    if (!pendingActionModal) return
    const trimmed = pendingActionReason.trim()
    if (!trimmed) {
      setPendingActionReasonError(
        pendingActionModal.kind === 'cancel'
          ? 'Informe o motivo do cancelamento.'
          : 'Informe o motivo da rejeição.'
      )
      return
    }
    if (trimmed.length < 3) {
      setPendingActionReasonError('O motivo deve ter pelo menos 3 caracteres.')
      return
    }

    setSubmittingPendingAction(true)
    setPendingActionReasonError(null)
    setActionError(null)
    try {
      const { kind, request } = pendingActionModal
      const updated =
        kind === 'cancel'
          ? await brokerCadastroChangeRequestsService.cancelChangeRequest(request.id, pendingActionReason)
          : await brokerCadastroChangeRequestsService.rejectChangeRequest(request.id, pendingActionReason)
      await logBrokerChangeRequestCancelOrReject(
        kind === 'cancel' ? 'cancel' : 'reject',
        request,
        updated,
        trimmed
      )
      if (selectedRequest?.id === request.id) {
        setSelectedRequest(updated)
      }
      closePendingActionModal()
      await load()
    } catch (error) {
      setPendingActionReasonError(getErrorMessage(error))
    } finally {
      setSubmittingPendingAction(false)
    }
  }

  const runApplyChangeRequest = async (request: HelpdeskBrokerChangeRequest) => {
    setApplyingRequestId(request.id)
    setSubmittingApply(true)
    setApplyModalError(null)
    setActionError(null)
    try {
      const updated = await brokerCadastroChangeRequestsService.applyChangeRequest(request.id)
      await logBrokerChangeRequestApply(request, updated)
      setApplyConfirmTarget(null)
      setApplyModalError(null)
      if (selectedRequest?.id === request.id) {
        setSelectedRequest(updated)
      }
      await load()
    } catch (error) {
      const msg = getErrorMessage(error)
      setApplyModalError(msg)
      if (selectedRequest?.id === request.id) {
        setActionError(msg)
      }
    } finally {
      setSubmittingApply(false)
      setApplyingRequestId(null)
    }
  }

  const handleApplyClick = (request: HelpdeskBrokerChangeRequest) => {
    if (!canActOnPendingRequest(request) || submittingApply || submittingPendingAction) return
    closePendingActionModal()
    setApplyModalError(null)
    setApplyConfirmTarget(request)
  }

  const handleConfirmApplyInModal = () => {
    if (!applyConfirmTarget || submittingApply) return
    void runApplyChangeRequest(applyConfirmTarget)
  }

  const closeApplyConfirmModal = () => {
    if (submittingApply) return
    setApplyConfirmTarget(null)
    setApplyModalError(null)
  }

  const renderRequestActionButtons = (request: HelpdeskBrokerChangeRequest) => {
    if (request.status !== 'pending' || permissionsLoading || !canCancel) {
      return null
    }
    return (
      <div className="flex shrink-0 flex-nowrap items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className={btnApplyOutlineClass}
          title="Aplicar alterações no cadastro (SCGP)"
          disabled={submittingApply || submittingPendingAction}
          onClick={() => handleApplyClick(request)}
        >
          {submittingApply && applyingRequestId === request.id ? 'Aplicando...' : 'Aplicar'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          className={btnRejectOutlineClass}
          title="Rejeitar solicitação pendente"
          disabled={submittingPendingAction || submittingApply}
          onClick={() => openRejectFlow(request)}
        >
          Rejeitar
        </Button>
        <Button
          variant="outline"
          size="sm"
          className={btnCancelOutlineClass}
          title="Cancelar solicitação pendente"
          disabled={submittingPendingAction || submittingApply}
          onClick={() => openCancelFlow(request)}
        >
          Cancelar
        </Button>
      </div>
    )
  }

  const requestTypeOptions = Object.entries(requestTypeLabels) as Array<[BrokerChangeRequestType, string]>

  return (
    <>
      <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard moduleName={MODULE_NAME} permissionType="show">
          <div className="h-full flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                    {moduleInfo?.display_name ?? 'Atualização cadastral'}
                  </Heading>
                  <Text size="sm" color="muted" className="mt-1">
                    {moduleInfo?.description ?? 'Gestão e auditoria das solicitações de atualização cadastral enviadas pelo portal.'}
                  </Text>
                </div>
                <Button variant="outline" className={btnRefreshOutlineClass} onClick={() => void load()} disabled={loading}>
                  Atualizar
                </Button>
              </div>
            </div>

            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 shadow-lg rounded-lg p-4 text-white">
                  <div className="text-center">
                    <Text size="2xl" className="text-white font-bold mb-1">{counts.pending}</Text>
                    <Text size="sm" className="text-white">Pendentes</Text>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-green-500 to-green-600 shadow-lg rounded-lg p-4 text-white">
                  <div className="text-center">
                    <Text size="2xl" className="text-white font-bold mb-1">{counts.applied}</Text>
                    <Text size="sm" className="text-white">Aplicadas</Text>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-red-500 to-red-600 shadow-lg rounded-lg p-4 text-white">
                  <div className="text-center">
                    <Text size="2xl" className="text-white font-bold mb-1">{counts.rejected}</Text>
                    <Text size="sm" className="text-white">Rejeitadas</Text>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-pink-500 to-rose-600 shadow-lg rounded-lg p-4 text-white">
                  <div className="text-center">
                    <Text size="2xl" className="text-white font-bold mb-1">{counts.cancelled}</Text>
                    <Text size="sm" className="text-white">Canceladas</Text>
                  </div>
                </div>
              </div>

              {/* Filtros */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                    ID SCGP (corretor)
                  </Text>
                  <Input
                    placeholder="Filtrar por ID SCGP..."
                    value={filtroIdScgp}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFiltroIdScgp(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div>
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                    Nome do corretor
                  </Text>
                  <Input
                    placeholder="Filtrar por nome..."
                    value={filtroNomeCorretor}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFiltroNomeCorretor(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div>
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                    Responsável da corretora
                  </Text>
                  <Select
                    value={displayedComercialUserId}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFiltroComercialUserId(e.target.value)}
                    disabled={isComercialFilterRestricted}
                    className="w-full"
                  >
                    {!isComercialFilterRestricted && <option value="">Todos</option>}
                    {comercialUsersOptions.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                    Status
                  </Text>
                  <Select
                    value={statusFilter}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setStatusFilter(e.target.value as StatusFilter)}
                    className="w-full"
                  >
                    <option value="all">Todos</option>
                    <option value="pending">Pendente</option>
                    <option value="applied">Aplicada</option>
                    <option value="rejected">Rejeitada</option>
                    <option value="cancelled">Cancelada</option>
                  </Select>
                </div>
                <div>
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                    Tipo
                  </Text>
                  <Select
                    value={typeFilter}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTypeFilter(e.target.value as RequestTypeFilter)}
                    className="w-full"
                  >
                    <option value="all">Todos</option>
                    {requestTypeOptions.map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button variant="outline" size="sm" className={btnFilterOutlineClass} onClick={clearFilters}>
                    Limpar Filtros
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex-1 px-6 py-4">
              {pageError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-900/20 dark:text-red-200">
                  {pageError}
                </div>
              )}

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                {loading ? (
                  <div className="p-12 flex items-center justify-center">
                    <LoadingSpinner />
                  </div>
                ) : pageRows.length === 0 ? (
                  <div className="p-12 text-center">
                    <Text color="muted">
                      Nenhuma solicitação encontrada.
                    </Text>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-900/50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Solicitado em</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Corretor</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Responsável</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tipo</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Solicitado por</th>
                          <th className="whitespace-nowrap px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Detalhes</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {pageRows.map((request) => (
                          <tr
                            key={request.id}
                            className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                          >
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                              {formatDateTime(request.requested_at)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                              <div className="font-medium">{request.broker?.name ?? 'Corretor sem nome'}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {request.broker?.id_scgp != null ? `SCGP ${request.broker.id_scgp}` : request.broker_id}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                              {assignmentByBrokerId.get(request.broker_id)?.name ?? '—'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                              {requestTypeLabels[request.request_type]}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                              <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${statusBadgeClass(request.status)}`}>
                                {statusLabels[request.status]}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                              {request.requester?.name ?? '—'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                              <Button
                                variant="outline"
                                size="sm"
                                className={btnDetailsOutlineClass}
                                onClick={() => setSelectedRequest(request)}
                              >
                                Ver detalhes
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <Text size="sm" color="muted">
                    Exibindo {pageRows.length} de {filtered.length} solicitações filtradas.
                  </Text>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>
                      Anterior
                    </Button>
                    <Text size="sm" color="muted">
                      Página {currentPage} de {totalPages}
                    </Text>
                    <Button variant="outline" size="sm" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                      Próxima
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </PermissionGuard>
      </div>

      {selectedRequest && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <div className="fixed inset-0 bg-black/40" onClick={closeDetail} />
            <div className="relative w-full max-w-3xl rounded-lg bg-white p-6 text-left shadow-xl dark:bg-gray-800">
              <div className="flex items-start justify-between gap-4 border-b border-gray-200 pb-4 dark:border-gray-700">
                <div>
                  <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                    Detalhe da solicitação
                  </Heading>
                  <Text size="sm" color="muted" className="mt-1">
                    {brokerLabel(selectedRequest)}
                  </Text>
                </div>
                <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${statusBadgeClass(selectedRequest.status)}`}>
                  {statusLabels[selectedRequest.status]}
                </span>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border-2 border-indigo-300 bg-gradient-to-br from-indigo-50 to-violet-50 p-4 shadow-sm dark:border-indigo-500/50 dark:from-indigo-950/50 dark:to-violet-950/40">
                <Text
                  size="xs"
                  weight="bold"
                  className="tracking-widest text-indigo-700 dark:text-indigo-300"
                >
                  TIPO
                </Text>
                <Text as="p" size="2xl" weight="semibold" className="text-indigo-950 dark:text-indigo-50">
                  {requestTypeLabels[selectedRequest.request_type]}
                </Text>
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Text size="xs" color="muted" className="uppercase tracking-wider">Solicitado por</Text>
                  <Text className="mt-1">{selectedRequest.requester?.name ?? '—'}</Text>
                </div>
                <div>
                  <Text size="xs" color="muted" className="uppercase tracking-wider">Solicitado em</Text>
                  <Text className="mt-1">{formatDateTime(selectedRequest.requested_at)}</Text>
                </div>
                <div>
                  <Text size="xs" color="muted" className="uppercase tracking-wider">Processado em</Text>
                  <Text className="mt-1">{formatDateTime(selectedRequest.processed_at)}</Text>
                </div>
                <div>
                  <Text size="xs" color="muted" className="uppercase tracking-wider">Processado por</Text>
                  <Text className="mt-1">{selectedRequest.processor?.name ?? selectedRequest.processed_by ?? '—'}</Text>
                </div>
                <div>
                  <Text size="xs" color="muted" className="uppercase tracking-wider">Referência externa</Text>
                  <Text className="mt-1">{selectedRequest.external_ref ?? '—'}</Text>
                </div>
              </div>

              {selectedRequest.status === 'rejected' && (
                <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-950 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-100">
                  <Text size="sm" weight="semibold" className="text-red-900 dark:text-red-100">
                    Rejeição
                  </Text>
                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <Text size="xs" className="uppercase tracking-wider text-red-700/80 dark:text-red-200/80">
                        Data da rejeição
                      </Text>
                      <Text size="sm" className="mt-1 text-red-900 dark:text-red-100">
                        {formatDateTime(selectedRequest.processed_at)}
                      </Text>
                    </div>
                    <div>
                      <Text size="xs" className="uppercase tracking-wider text-red-700/80 dark:text-red-200/80">
                        Responsável
                      </Text>
                      <Text size="sm" className="mt-1 text-red-900 dark:text-red-100">
                        {selectedRequest.processor?.name ?? selectedRequest.processed_by ?? '—'}
                      </Text>
                    </div>
                  </div>
                  <div className="mt-3 border-t border-red-200/80 pt-3 dark:border-red-800/50">
                    <Text size="xs" className="uppercase tracking-wider text-red-700/80 dark:text-red-200/80">
                      Motivo da rejeição
                    </Text>
                    <Text size="sm" className="mt-1 whitespace-pre-wrap text-red-900/90 dark:text-red-100/90">
                      {selectedRequest.rejection_reason?.trim() ? selectedRequest.rejection_reason : '—'}
                    </Text>
                  </div>
                </div>
              )}

              {selectedRequest.status === 'cancelled' && (
                <div className="mt-4 rounded-lg border border-pink-200 bg-pink-50 p-4 text-sm text-pink-950 dark:border-pink-800/50 dark:bg-pink-950/35 dark:text-pink-100">
                  <Text size="sm" weight="semibold" className="text-pink-900 dark:text-pink-100">
                    Cancelamento
                  </Text>
                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <Text size="xs" className="uppercase tracking-wider text-pink-700/80 dark:text-pink-200/80">
                        Data do cancelamento
                      </Text>
                      <Text size="sm" className="mt-1 text-pink-900 dark:text-pink-100">
                        {formatDateTime(selectedRequest.processed_at)}
                      </Text>
                    </div>
                    <div>
                      <Text size="xs" className="uppercase tracking-wider text-pink-700/80 dark:text-pink-200/80">
                        Responsável
                      </Text>
                      <Text size="sm" className="mt-1 text-pink-900 dark:text-pink-100">
                        {selectedRequest.processor?.name ?? selectedRequest.processed_by ?? '—'}
                      </Text>
                    </div>
                  </div>
                  <div className="mt-3 border-t border-pink-200/80 pt-3 dark:border-pink-700/50">
                    <Text size="xs" className="uppercase tracking-wider text-pink-700/80 dark:text-pink-200/80">
                      Motivo do cancelamento
                    </Text>
                    <Text size="sm" className="mt-1 whitespace-pre-wrap text-pink-900/90 dark:text-pink-100/90">
                      {selectedRequest.rejection_reason?.trim() ? selectedRequest.rejection_reason : '—'}
                    </Text>
                  </div>
                </div>
              )}

              {selectedRequest.status === 'applied' && (
                <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-950 dark:border-emerald-800/50 dark:bg-emerald-950/35 dark:text-emerald-100">
                  <Text size="sm" weight="semibold" className="text-emerald-900 dark:text-emerald-100">
                    Aplicação
                  </Text>
                  <Text size="sm" className="mt-2 text-emerald-900/90 dark:text-emerald-100/90">
                    Esta solicitação foi aplicada: os dados foram atualizados no cadastro (SCGP).
                  </Text>
                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <Text size="xs" className="uppercase tracking-wider text-emerald-700/80 dark:text-emerald-200/80">
                        Data da aplicação
                      </Text>
                      <Text size="sm" className="mt-1 text-emerald-900 dark:text-emerald-100">
                        {formatDateTime(selectedRequest.processed_at)}
                      </Text>
                    </div>
                    <div>
                      <Text size="xs" className="uppercase tracking-wider text-emerald-700/80 dark:text-emerald-200/80">
                        Responsável pela aplicação
                      </Text>
                      <Text size="sm" className="mt-1 text-emerald-900 dark:text-emerald-100">
                        {selectedRequest.processor?.name ?? selectedRequest.processed_by ?? '—'}
                      </Text>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6">
                <Text size="sm" weight="semibold" className="mb-1 text-gray-900 dark:text-white">
                  Alteração solicitada
                </Text>
                {selectedRequest.request_type !== 'contact_create' && (
                  <Text size="xs" color="muted" className="mb-3">
                    {selectedRequest.status === 'pending'
                      ? 'Comparação com o cadastro atual antes de aplicar.'
                      : 'Comparação com o cadastro no momento da consulta.'}
                  </Text>
                )}
                {selectedRequest.request_type === 'contact_create' && (
                  <Text size="xs" color="muted" className="mb-3">
                    Novos valores solicitados para inclusão no cadastro.
                  </Text>
                )}
                {detailBaselineLoading ? (
                  <div className="flex items-center justify-center rounded-lg border border-gray-200 py-8 dark:border-gray-700">
                    <LoadingSpinner />
                  </div>
                ) : (
                  <>
                    {detailBaselineError && (
                      <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100">
                        {detailBaselineError}
                      </div>
                    )}
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                      <RequestChangeDiffTable rows={detailDiffRows} />
                    </div>
                  </>
                )}
              </div>

              {actionError && (
                <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-900/20 dark:text-red-200">
                  {actionError}
                </div>
              )}

              <div
                className={`mt-6 flex flex-col gap-3 sm:flex-row sm:items-center ${
                  selectedRequest.status === 'pending' ? 'sm:justify-between' : 'sm:justify-end'
                }`}
              >
                {renderRequestActionButtons(selectedRequest)}
                <Button variant="outline" onClick={closeDetail} className="shrink-0 self-end sm:self-center">
                  Fechar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {pendingActionModal && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <div className="fixed inset-0 bg-black/50" onClick={() => !submittingPendingAction && closePendingActionModal()} />
            <div
              className={`relative w-full max-w-lg rounded-lg bg-white p-6 text-left shadow-xl dark:bg-gray-800 ${
                pendingActionModal.kind === 'reject'
                  ? 'ring-2 ring-red-200 dark:ring-red-900/50'
                  : ''
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                {pendingActionModal.kind === 'cancel' ? 'Cancelar solicitação' : 'Rejeitar solicitação'}
              </Heading>
              <Text size="sm" color="muted" className="mt-2">
                {brokerLabel(pendingActionModal.request)}
              </Text>
              <div className="mt-4">
                <Text size="sm" weight="semibold" className="mb-2 text-gray-900 dark:text-white">
                  {pendingActionModal.kind === 'cancel'
                    ? 'Motivo do cancelamento (obrigatório)'
                    : 'Motivo da rejeição (obrigatório)'}
                </Text>
                <textarea
                  className={cancelReasonTextareaClass}
                  placeholder={
                    pendingActionModal.kind === 'cancel'
                      ? 'Descreva o motivo do cancelamento...'
                      : 'Descreva o motivo da rejeição...'
                  }
                  value={pendingActionReason}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    setPendingActionReason(e.target.value)
                    if (pendingActionReasonError) setPendingActionReasonError(null)
                  }}
                  disabled={submittingPendingAction}
                  rows={4}
                  aria-invalid={!!pendingActionReasonError}
                />
                {pendingActionReasonError && (
                  <Text size="sm" className="mt-2 text-red-600 dark:text-red-400">
                    {pendingActionReasonError}
                  </Text>
                )}
              </div>
              <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
                <Button variant="outline" onClick={closePendingActionModal} disabled={submittingPendingAction}>
                  Voltar
                </Button>
                <Button
                  variant="outline"
                  onClick={() => void submitPendingActionReason()}
                  disabled={submittingPendingAction}
                  className={
                    pendingActionModal.kind === 'cancel' ? btnCancelOutlineClass : btnRejectOutlineClass
                  }
                >
                  {submittingPendingAction
                    ? pendingActionModal.kind === 'cancel'
                      ? 'Cancelando...'
                      : 'Rejeitando...'
                    : pendingActionModal.kind === 'cancel'
                      ? 'Confirmar cancelamento'
                      : 'Confirmar rejeição'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {applyConfirmTarget && (
        <div className="fixed inset-0 z-[61] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <div
              className="fixed inset-0 bg-black/50"
              onClick={() => !submittingApply && closeApplyConfirmModal()}
            />
            <div
              className="relative w-full max-w-lg rounded-lg bg-white p-6 text-left shadow-xl ring-2 ring-emerald-200 dark:bg-gray-800 dark:ring-emerald-900/40"
              onClick={(e) => e.stopPropagation()}
            >
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                Aplicar alterações
              </Heading>
              <Text size="sm" color="muted" className="mt-2">
                {brokerLabel(applyConfirmTarget)}
              </Text>
              <Text size="sm" className="mt-4 text-gray-800 dark:text-gray-200">
                {SCGP_APPLY_CONFIRM_MESSAGE}
              </Text>
              {applyModalError && (
                <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-900/20 dark:text-red-200">
                  {applyModalError}
                </div>
              )}
              <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
                <Button variant="outline" onClick={closeApplyConfirmModal} disabled={submittingApply}>
                  Voltar
                </Button>
                <Button
                  variant="outline"
                  className={btnApplyOutlineClass}
                  onClick={handleConfirmApplyInModal}
                  disabled={submittingApply}
                >
                  {submittingApply ? 'Aplicando...' : 'Confirmar'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default AtualizacaoCadastral
